import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Collapse, Empty, Select, Skeleton, Table, Tag } from 'antd';
import DataTable from '@/components/data-table';
import TruncateTooltipCell from '@/components/data-table/TruncateTooltipCell';
import { Sidebar } from '@/components/sidebar';
import { DataTableSkeleton } from '@/components/skeletons';
import { API } from '@/services/api.service';
import { Facts, ListToolbar } from './Filters';
import { columnKeysOf, date, listQueryOf, money, noop, str } from './helpers';
import useColumnHeaders from './useColumnHeaders';

const GROUPS = ['amountDiff', 'numberDiff', 'stateOnly', 'booksOnly', 'matched', 'noBooks'];
const GROUP_COLORS = { matched: 'green', amountDiff: 'orange', numberDiff: 'gold', stateOnly: 'red', booksOnly: 'purple', noBooks: 'default' };
const FILTERS = ['group', 'pib', 'partnerName', 'document', 'source', 'positions', 'vatDate'];
const SORTS = ['group', 'pib', 'partnerName', 'document', 'source', 'positions', 'vatDate', 'stateVat', 'booksVat', 'difference'];
const KEYS = columnKeysOf(FILTERS, SORTS);
const SUMMARY_KEY = 'sefState.inputVat.summaryOpen';

const readOpen = () => {
  try {
    return localStorage.getItem(SUMMARY_KEY) !== '0';
  } catch {
    return true;
  }
};

/**
 * Input VAT as the state computes it (ppppdv) for one month, against the
 * book of incoming invoices in AB: the month's totals and positions (folds
 * away to give the table room), then document by document what matches,
 * what only the state has, what only the books have and where amounts or
 * numbers differ. A row opens both sides next to each other.
 */
export default function InputVatTab({ company, queryParams, setQueryParams }) {
  const { t } = useTranslation('sefState');
  const [selected, setSelected] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(readOpen);
  const periods = useQuery({
    queryKey: ['sefState', company, 'inputVatPeriods'],
    queryFn: () => API.sefState.getInputVatPeriods(company),
    retry: false,
  });
  const period = str(queryParams.period);
  useEffect(() => {
    if (!period && periods.data?.length) {
      const [p] = periods.data;
      setQueryParams({ period: `${p.year}-${String(p.month).padStart(2, '0')}` });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periods.data, period]);

  const [year, month] = period ? period.split('-').map(Number) : [];
  const query = listQueryOf(queryParams);
  const monthQuery = useQuery({
    queryKey: ['sefState', company, 'inputVat', period, query],
    queryFn: () => API.sefState.getInputVatMonth(company, year, month, query),
    enabled: Boolean(year && month),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const { data } = monthQuery;
  const h = useColumnHeaders(queryParams, setQueryParams, FILTERS);
  const c = (key) => t(`inputVat.columns.${key}`);
  const groupOptions = GROUPS.map((g) => ({ value: g, label: t(`inputVat.groups.${g}`) }));
  const sourceOptions = (data?.sources || []).map((s) => ({ value: s, label: s }));

  const toggleSummary = (keys) => {
    const open = keys.includes('summary');
    setSummaryOpen(open);
    try {
      localStorage.setItem(SUMMARY_KEY, open ? '1' : '0');
    } catch {
      // per-viewer convenience only
    }
  };

  if (periods.isSuccess && !periods.data.length) return <Empty description={t('inputVat.noData')} />;

  const columns = [
    {
      title: h.options('group', c('group'), groupOptions),
      dataIndex: 'group',
      width: 170,
      render: (g) => <Tag color={GROUP_COLORS[g]}>{t(`inputVat.groups.${g}`)}</Tag>,
    },
    { title: h.text('pib', c('pib')), dataIndex: 'pib', width: 140 },
    { title: h.text('partnerName', c('partnerName')), dataIndex: 'partnerName', width: 220, render: (v) => <TruncateTooltipCell label={v} /> },
    {
      title: h.text('document', c('document')),
      dataIndex: 'document',
      width: 200,
      render: (v, r) => (
        <div className="min-w-0">
          <TruncateTooltipCell label={v} />
          {r.booksDocument && r.booksDocument !== v && <TruncateTooltipCell label={`AB: ${r.booksDocument}`} className="text-xs opacity-60" />}
        </div>
      ),
    },
    { title: h.options('source', c('source'), sourceOptions), dataIndex: 'source', width: 190, render: (v) => <TruncateTooltipCell label={v} /> },
    {
      title: h.text('positions', c('positions')),
      dataIndex: 'positions',
      width: 130,
      render: (v, r) => <TruncateTooltipCell label={[v, r.booksPopdv && `AB ${r.booksPopdv}`].filter(Boolean).join(' · ')} />,
    },
    { title: h.date('vatDate', c('vatDate')), dataIndex: 'vatDate', width: 130, render: date },
    { title: h.sort('stateVat', c('stateVat')), dataIndex: 'stateVat', width: 140, align: 'right', render: (v) => (v == null ? '—' : money(v)) },
    { title: h.sort('booksVat', c('booksVat')), dataIndex: 'booksVat', width: 140, align: 'right', render: (v) => (v == null ? '—' : money(v)) },
    {
      title: h.sort('difference', c('difference')),
      dataIndex: 'difference',
      width: 140,
      align: 'right',
      render: (v) => (v == null || v === 0 ? '' : <span className="font-semibold text-orange-600">{money(v)}</span>),
    },
  ];

  const labels = {
    ...Object.fromEntries(
      ['group', 'pib', 'partnerName', 'document', 'source', 'positions', 'vatDate', 'stateVat', 'booksVat', 'difference', 'booksDocument', 'booksPopdv', 'booksOrder'].map((k) => [k, c(k)])
    ),
    ...Object.fromEntries(GROUPS.map((g) => [`group_${g}`, t(`inputVat.groups.${g}`)])),
  };

  const summary = data && (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
      <div>
        <div className="flex flex-wrap gap-6">
          <div>
            <div className="text-xs opacity-60">{c('stateVat')}</div>
            <div className="text-lg font-semibold">{money(data.totals.stateVat)}</div>
          </div>
          {data.totals.booksVat != null && (
            <>
              <div>
                <div className="text-xs opacity-60">{c('booksVat')}</div>
                <div className="text-lg font-semibold">{money(data.totals.booksVat)}</div>
              </div>
              <div>
                <div className="text-xs opacity-60">{c('difference')}</div>
                <div className="text-lg font-semibold text-orange-600">{money(data.totals.stateVat - data.totals.booksVat)}</div>
              </div>
            </>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {GROUPS.filter((g) => data.summary[g]?.count).map((g) => (
            <Tag
              key={g}
              color={GROUP_COLORS[g]}
              className="cursor-pointer rounded-full px-3"
              onClick={() => setQueryParams({ group: str(queryParams.group) === g ? undefined : [g], offset: 1 })}
            >
              {t(`inputVat.groups.${g}`)}: {data.summary[g].count}
            </Tag>
          ))}
        </div>
        <p className="mt-3 text-xs opacity-60">
          {data.dataCollectionDate && `${t('inputVat.collected')} ${date(data.dataCollectionDate)} · `}
          {t('inputVat.fetched')} {date(data.fetchedAt)}
          {data.records?.length > 0 && ` · ${t('inputVat.records')}: ${data.records.map((r) => `${r.number} (${r.status})`).join(', ')}`}
        </p>
      </div>
      <Table
        size="small"
        rowKey="position"
        pagination={false}
        dataSource={data.positions}
        columns={[
          { title: t('inputVat.position'), dataIndex: 'position', width: 80 },
          { title: t('inputVat.count'), dataIndex: 'count', width: 70, align: 'right' },
          { title: t('inputVat.base20'), dataIndex: 'base20', align: 'right', render: money },
          { title: t('inputVat.vat20'), dataIndex: 'vat20', align: 'right', render: money },
          { title: t('inputVat.base10'), dataIndex: 'base10', align: 'right', render: money },
          { title: t('inputVat.vat10'), dataIndex: 'vat10', align: 'right', render: money },
        ]}
      />
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm opacity-70">{t('inputVat.month')}</span>
        <Select
          className="w-[180px]"
          value={period || undefined}
          loading={periods.isLoading}
          onChange={(v) => setQueryParams({ period: v, offset: 1 })}
          options={(periods.data || []).map((p) => ({
            value: `${p.year}-${String(p.month).padStart(2, '0')}`,
            label: `${String(p.month).padStart(2, '0')}/${p.year} · ${p.documentsCount}`,
          }))}
        />
      </div>

      {monthQuery.error && <Alert type="warning" showIcon message={t('inputVat.noMonth')} />}
      {data && !data.booksAvailable && <Alert type="info" showIcon message={t('inputVat.noBooks')} />}

      {(periods.isLoading || (monthQuery.isLoading && !data)) && (
        <>
          <Skeleton active paragraph={{ rows: 3 }} />
          <DataTableSkeleton columnLength={6} />
        </>
      )}

      {data && (
        <Collapse
          className="rounded-xl bg-white"
          activeKey={summaryOpen ? ['summary'] : []}
          onChange={toggleSummary}
          items={[
            {
              key: 'summary',
              label: (
                <span className="font-semibold">
                  {t('inputVat.totals')} · {c('stateVat')} {money(data.totals.stateVat)}
                  {data.totals.booksVat != null && ` · ${c('booksVat')} ${money(data.totals.booksVat)}`}
                </span>
              ),
              children: summary,
            },
          ]}
        />
      )}

      {data && (
        <DataTable
          rowKey={(r) => `${r.group}|${r.pib}|${r.document}|${r.booksDocument}`}
          title={`${t('inputVat.title')} · ${data.count}`}
          data={data.items}
          total={data.count}
          status={monthQuery.isLoading ? 'pending' : 'success'}
          columns={columns}
          offset={queryParams.offset}
          limit={queryParams.limit}
          onChangePagination={noop}
          onRow={(r) => ({ onClick: () => setSelected(r), className: 'cursor-pointer' })}
          tableLayout="fixed"
          scroll={{ x: columns.reduce((s, col) => s + (col.width || 0), 0) }}
          size="small"
          headerChildren={
            <ListToolbar
              keys={KEYS}
              setQueryParams={setQueryParams}
              exportProps={{ company, kind: 'inputVat', filters: { ...query, year, month }, labels, disabled: !data.count }}
            />
          }
        />
      )}

      <Sidebar
        title={selected?.document || selected?.booksDocument || t('inputVat.title')}
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        className="max-w-xl"
      >
        {selected && (
          <div className="grid gap-4 p-4">
            <Tag color={GROUP_COLORS[selected.group]} className="w-max">
              {t(`inputVat.groups.${selected.group}`)}
            </Tag>
            <Facts
              items={[
                [c('pib'), selected.pib],
                [c('partnerName'), selected.partnerName],
                [c('vatDate'), date(selected.vatDate)],
              ]}
            />
            <h4 className="font-semibold">{t('inputVat.detail.state')}</h4>
            <Facts
              items={[
                [c('document'), selected.stateVat == null ? '—' : selected.document],
                [c('source'), selected.source],
                [c('positions'), selected.positions],
                [t('inputVat.detail.status'), selected.status],
                [t('inputVat.detail.base'), selected.stateBase == null ? '—' : money(selected.stateBase)],
                [c('stateVat'), selected.stateVat == null ? '—' : money(selected.stateVat)],
              ]}
            />
            <h4 className="font-semibold">{t('inputVat.detail.books')}</h4>
            <Facts
              items={[
                [c('booksDocument'), selected.booksDocument || '—'],
                [t('inputVat.detail.documentType'), selected.booksPopdv || '—'],
                [c('booksOrder'), selected.booksOrder || '—'],
                [t('inputVat.detail.base'), selected.booksBase == null ? '—' : money(selected.booksBase)],
                [c('booksVat'), selected.booksVat == null ? '—' : money(selected.booksVat)],
              ]}
            />
            {selected.difference != null && selected.difference !== 0 && (
              <Alert type="warning" showIcon message={`${c('difference')}: ${money(selected.difference)}`} />
            )}
            <p className="text-xs opacity-70">{t(`inputVat.explain.${selected.group}`)}</p>
          </div>
        )}
      </Sidebar>
    </div>
  );
}
